import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * KineticBanner — replaces the boring scrolling marquee with a kinetic
 * typography card that cycles slogans with a stamp/glitch effect.
 *
 * - One phrase is on stage at a time, big & centered
 * - Letters animate in with a stagger (Y-blur drop)
 * - A scan-line + grid backdrop add motion without scrolling text
 */
export default function KineticBanner({
  phrases = [
    'EVENTOS ÚNICOS, GRANDES EXPERIENCIAS',
    'MOMENTO E.U.G.E.',
    'BUENOS AIRES NIGHTLIFE',
    'PRODUCTORAS · DJs · COMUNIDAD',
  ],
  variant = 'gold', // gold | dark
  interval = 3200,
}) {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI(x => (x + 1) % phrases.length), interval);
    return () => clearInterval(t);
  }, [phrases.length, interval]);

  const isGold = variant === 'gold';
  const bgClass = isGold ? 'bg-[#D4AF37]' : 'bg-[#0A0A0A]';
  const textClass = isGold ? 'text-black' : 'text-[#D4AF37]';
  const accent = isGold ? 'rgba(0,0,0,0.18)' : 'rgba(212,175,55,0.18)';

  const current = phrases[i] || '';
  const letters = current.split('');

  return (
    <div
      className={`relative overflow-hidden ${bgClass} py-3 sm:py-4 border-y border-white/5`}
      data-testid="home-kinetic-banner"
      style={{ fontFamily: "'Unbounded', sans-serif" }}
    >
      {/* Soft animated scanline (subtle motion, no scroll lines) */}
      <motion.div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px] pointer-events-none"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        animate={{ y: ['0%', '100%', '0%'] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Stage */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-[36px] sm:min-h-[44px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="flex items-center justify-center text-center"
          >
            {letters.map((char, idx) => (
              <motion.span
                key={`${i}-${idx}`}
                initial={{ y: '110%', opacity: 0, filter: 'blur(8px)', rotate: -8 }}
                animate={{ y: '0%', opacity: 1, filter: 'blur(0px)', rotate: 0 }}
                exit={{ y: '-90%', opacity: 0, filter: 'blur(6px)', rotate: 5 }}
                transition={{
                  delay: idx * 0.022,
                  duration: 0.38,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`inline-block text-[clamp(14px,2.4vw,24px)] font-black tracking-tight uppercase ${textClass}`}
                style={{ whiteSpace: char === ' ' ? 'pre' : 'normal' }}
              >
                {char}
              </motion.span>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Tiny progress dots */}
      <div className="relative z-10 flex items-center justify-center gap-1.5 mt-1.5">
        {phrases.map((_, idx) => (
          <span
            key={idx}
            aria-hidden
            className="block h-[2px] rounded-full transition-all"
            style={{
              width: idx === i ? 16 : 5,
              background: idx === i ? (isGold ? '#000' : '#D4AF37') : 'rgba(0,0,0,0.25)',
              opacity: isGold ? 1 : (idx === i ? 1 : 0.4),
            }}
          />
        ))}
      </div>

      {/* Corner accents */}
      <span aria-hidden className={`absolute top-1.5 left-1.5 w-2 h-2 border-t-2 border-l-2 ${isGold ? 'border-black/40' : 'border-[#D4AF37]/60'}`} />
      <span aria-hidden className={`absolute top-1.5 right-1.5 w-2 h-2 border-t-2 border-r-2 ${isGold ? 'border-black/40' : 'border-[#D4AF37]/60'}`} />
      <span aria-hidden className={`absolute bottom-1.5 left-1.5 w-2 h-2 border-b-2 border-l-2 ${isGold ? 'border-black/40' : 'border-[#D4AF37]/60'}`} />
      <span aria-hidden className={`absolute bottom-1.5 right-1.5 w-2 h-2 border-b-2 border-r-2 ${isGold ? 'border-black/40' : 'border-[#D4AF37]/60'}`} />
    </div>
  );
}
